import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { auth, db, loginWithGoogle, loginWithMicrosoft, loginWithPassword, registerWithPassword, resetPassword } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import {
  PUBLIC_SITE_CONTENT_COLLECTION,
  LOGIN_PAGE_DOC_ID,
  resolveLoginFooterHtml,
  resolveLoginNoticeHtml,
} from '../constants/loginPageContent';
import { LEGAL_LINKS } from '../constants/legalLinks';
import { AUTH_MAGIC_LINK_SENDER } from '../constants/authEmail';
import { SHOW_MICROSOFT_SIGNIN } from '../constants/authProviders';
import { MIN_PASSWORD_LENGTH } from '../constants/passwordPolicy';
import { EcoIntelLoadOverlay } from './EcoIntelLoader';
import './LoginPage.css';

// Email shown in the "Need help?" line under the auth card.
const SUPPORT_EMAIL = 'accounts@ecointel.io';

const EDX_RAMP = ['#6B1E1E', '#B24D22', '#D9A72A', '#FED976', '#C9C943', '#7BAF44', '#3E9345', '#1F6E3D', '#0B4D26'];

// Loose email validation — Firebase Auth does the authoritative check; this is
// just to catch obvious typos before a network round-trip.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login({ isRegister = false }) {
  const navigate = useNavigate();
  const [loginPageHtml, setLoginPageHtml] = useState(() => ({
    footerInner: resolveLoginFooterHtml(null),
    notice: resolveLoginNoticeHtml(null),
  }));
  // 'default' → provider chooser; 'email' → email/password form; 'forgot' →
  // password-reset request; 'sent-reset' → reset-email confirmation.
  // 'sent-link-google' / 'sent-link-microsoft' are account-linking variants.
  const [mode, setMode] = useState('default');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sentToEmail, setSentToEmail] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [microsoftBusy, setMicrosoftBusy] = useState(false);
  const [emailError, setEmailError] = useState('');
  // Sign-in vs sign-up toggles copy and which handler runs on the email form.
  const [registerMode, setRegisterMode] = useState(isRegister);
  // Brief branded loading flash while we swap the copy, so the change feels
  // intentional rather than an instant text-flip.
  const [switching, setSwitching] = useState(false);

  const toggleAuthMode = () => {
    if (switching) return;
    setSwitching(true);
    setEmailError('');
    setPassword('');
    setConfirmPassword('');
    window.setTimeout(() => {
      setRegisterMode((v) => !v);
      setSwitching(false);
    }, 650);
  };

  // 1. Redirect if already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/dashboard');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // 2. Public login page copy from Firestore (editable in Admin / Superuser consoles)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, PUBLIC_SITE_CONTENT_COLLECTION, LOGIN_PAGE_DOC_ID));
        const data = snap.exists() ? snap.data() : null;
        if (cancelled) return;
        setLoginPageHtml({
          footerInner: resolveLoginFooterHtml(data),
          notice: resolveLoginNoticeHtml(data),
        });
      } catch (e) {
        console.error('Login page content fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Shared error surfacing for the OAuth popup providers (Google + Microsoft).
  const handleOAuthError = (error) => {
    console.error('Login Error:', error?.code, error?.message, error);
    const code = error?.code || '';
    if (code === 'auth/unauthorized-domain') {
      alert(
        'This site’s domain is not authorized for sign-in yet. In Firebase Console → Authentication → Settings → Authorized domains, add your host (e.g. app.ecointel.io).'
      );
    } else if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      alert('Sign-in was blocked or closed. Allow pop-ups for this site and try again.');
    } else {
      alert(`Login failed${code ? ` (${code})` : ''}. Please try again.`);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleBusy(true);
    try {
      const result = await loginWithGoogle();
      // loginWithGoogle returns { pendingEmailLink: true, email } when the
      // existing account uses a different provider. Auto-link by surfacing the
      // "check your email to confirm linking Google" state — the magic link
      // has already been sent inside loginWithGoogle().
      if (result?.pendingEmailLink) {
        setSentToEmail(result.email || '');
        setMode('sent-link-google');
        return;
      }
      // Otherwise the onAuthStateChanged effect above will redirect.
    } catch (error) {
      handleOAuthError(error);
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftBusy(true);
    try {
      const result = await loginWithMicrosoft();
      // Same account-linking contract as Google — see handleGoogleLogin.
      if (result?.pendingEmailLink) {
        setSentToEmail(result.email || '');
        setMode('sent-link-microsoft');
        return;
      }
      // Otherwise the onAuthStateChanged effect above will redirect.
    } catch (error) {
      handleOAuthError(error);
    } finally {
      setMicrosoftBusy(false);
    }
  };

  const mapPasswordError = (error, { forgot = false } = {}) => {
    console.error('Password auth error:', error?.code, error?.message, error);
    const code = error?.code || '';
    if (code === 'auth/invalid-email') return 'That email address looks invalid.';
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
      return forgot
        ? 'If an account exists for that email, we sent a reset link.'
        : 'Email or password is incorrect.';
    }
    if (code === 'auth/email-already-in-use') return 'An account already exists for this email. Sign in instead.';
    if (code === 'auth/weak-password') {
      return `Choose a stronger password (at least ${MIN_PASSWORD_LENGTH} characters with upper, lower, number, and symbol).`;
    }
    if (code === 'auth/too-many-requests') return 'Too many attempts. Please wait a few minutes and try again.';
    if (code === 'auth/operation-not-allowed') {
      return 'Email/password sign-in is not enabled yet. Contact support.';
    }
    return `Could not continue${code ? ` (${code})` : ''}. Please try again.`;
  };

  const handlePasswordSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setEmailError('Please enter your password.');
      return;
    }
    if (registerMode) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setEmailError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (password !== confirmPassword) {
        setEmailError('Passwords do not match.');
        return;
      }
    }
    setEmailError('');
    setEmailBusy(true);
    try {
      if (registerMode) {
        await registerWithPassword(trimmed, password);
      } else {
        await loginWithPassword(trimmed, password);
      }
      // onAuthStateChanged redirects to /dashboard.
    } catch (error) {
      setEmailError(mapPasswordError(error));
    } finally {
      setEmailBusy(false);
    }
  };

  const handleForgotPassword = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setEmailBusy(true);
    try {
      await resetPassword(trimmed);
      setSentToEmail(trimmed);
      setMode('sent-reset');
    } catch (error) {
      setEmailError(mapPasswordError(error, { forgot: true }));
    } finally {
      setEmailBusy(false);
    }
  };

  const resetToDefault = () => {
    setMode('default');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setSentToEmail('');
    setEmailError('');
  };

  return (
    <div className="login-page">
      {/* Promo / announcement banners are intentionally NOT shown here — they
          are reserved for signed-in users in the app shell (see Layout →
          TopBanner). Handoff 20 §D: two-panel chrome; auth handlers unchanged. */}
      <main className="login-wrap">
        <section className="login-brand" aria-label="EcoIntel brand">
          <div className="login-brand-lockup">
            <img className="login-lk-mark" src="/ei-mark.svg" alt="" aria-hidden="true" />
            <div className="login-lk-txt">
              <b>Ecological Intelligence</b>
              <span>understand your land</span>
            </div>
          </div>

          <div className="login-brand-mid">
            <div className="login-brand-eyebrow">EcoIntel, the Land Health System</div>
            <h1 className="login-brand-h">Understand your land.</h1>
            <p className="login-brand-sub">
              EcoIntel gives stewards deep insights into land health and functioning, plus assessments and actionable insights.
            </p>
            <p className="login-brand-sub2">
              Satellite-derived ecosystem condition, every parcel, every year, back to 2018.
            </p>
            <p className="login-brand-sub2">
              Disclosure-ready reporting for farmers, foresters, landowners and ESG teams: one analysis, delivered to multiple frameworks.
            </p>
          </div>

          <div className="login-brand-foot">
            <div className="login-ramp-cap">
              <span>A Beaufort scale of land health</span>
              <span>Nine levels</span>
            </div>
            <div className="login-ramp" aria-hidden="true">
              {EDX_RAMP.map((c) => (
                <span key={c} style={{ background: c }} />
              ))}
            </div>
          </div>
        </section>

        <section className="login-panel">
          <div className="login-panel-in">
            <EcoIntelLoadOverlay show={switching} message="" className="rounded-[20px]" lottiePixelWidth={120} lottiePixelHeight={150}>
              <div className="login-card">
                {mode === 'default' && (
                  <>
                    <h1>{registerMode ? 'Create your account' : 'Welcome back'}</h1>
                    <p className="login-lead">
                      {registerMode
                        ? 'Understand, track, and improve the health of your land.'
                        : 'Sign in to access your farms and analytics.'}
                    </p>

                    <div className="login-sso">
                      <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={googleBusy}
                        className="login-btn login-btn-google"
                      >
                        <span className="login-g-chip" aria-hidden="true">
                          <svg width="17" height="17" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                          </svg>
                        </span>
                        {googleBusy ? 'Opening Google…' : (registerMode ? 'Sign up with Google' : 'Sign in with Google')}
                      </button>

                      {SHOW_MICROSOFT_SIGNIN && (
                        <button
                          type="button"
                          onClick={handleMicrosoftLogin}
                          disabled={microsoftBusy}
                          className="login-btn login-btn-sso"
                        >
                          <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
                            <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                            <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
                            <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
                            <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
                          </svg>
                          {microsoftBusy ? 'Opening Microsoft…' : (registerMode ? 'Sign up with Microsoft' : 'Sign in with Microsoft')}
                        </button>
                      )}
                    </div>

                    <div className="login-divider"><span>OR</span></div>

                    <button
                      type="button"
                      onClick={() => setMode('email')}
                      className="login-btn login-btn-sso"
                    >
                      <Mail size={18} aria-hidden="true" />
                      Continue with email
                    </button>

                    <p className="login-signup">
                      {registerMode ? 'Already have an account?' : "Don't have an account yet?"}{' '}
                      <button type="button" onClick={toggleAuthMode} disabled={switching}>
                        {registerMode ? 'Sign in' : 'Sign up'}
                      </button>
                    </p>

                    <div className="login-card-note">
                      <p className="help">
                        Need help? Email{' '}
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                      </p>
                      <p className="terms">
                        By signing in, you agree to our{' '}
                        <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">Terms</a>
                        {' '}and{' '}
                        <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                      </p>
                    </div>
                  </>
                )}

                {mode === 'email' && (
                  <>
                    <h2>{registerMode ? 'Create your account' : 'Sign in with email'}</h2>
                    <p className="login-lead">
                      {registerMode
                        ? 'Create a password to access your account. You can explore the demo while your email is verified.'
                        : 'Enter your email and password to sign in.'}
                    </p>

                    <form onSubmit={handlePasswordSubmit} className="login-email-form">
                      <label htmlFor="email-password-input">Email address</label>
                      <input
                        id="email-password-input"
                        type="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                        placeholder="you@example.com"
                        disabled={emailBusy}
                      />
                      <label htmlFor="password-input">Password</label>
                      <input
                        id="password-input"
                        type="password"
                        autoComplete={registerMode ? 'new-password' : 'current-password'}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (emailError) setEmailError(''); }}
                        placeholder={registerMode ? `At least ${MIN_PASSWORD_LENGTH} characters` : 'Your password'}
                        disabled={emailBusy}
                      />
                      {registerMode && (
                        <>
                          <label htmlFor="confirm-password-input">Confirm password</label>
                          <input
                            id="confirm-password-input"
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => { setConfirmPassword(e.target.value); if (emailError) setEmailError(''); }}
                            placeholder="Re-enter your password"
                            disabled={emailBusy}
                          />
                          <p className="login-password-hint">
                            Use at least {MIN_PASSWORD_LENGTH} characters with upper, lower, number, and symbol.
                          </p>
                        </>
                      )}
                      {emailError && <p className="login-email-error">{emailError}</p>}
                      <button
                        type="submit"
                        disabled={emailBusy}
                        className="login-btn login-btn-google"
                      >
                        {emailBusy
                          ? (registerMode ? 'Creating account…' : 'Signing in…')
                          : (registerMode ? 'Create account' : 'Sign in')}
                      </button>
                      {!registerMode && (
                        <button
                          type="button"
                          className="login-forgot-link"
                          onClick={() => { setEmailError(''); setMode('forgot'); }}
                          disabled={emailBusy}
                        >
                          Forgot password?
                        </button>
                      )}
                    </form>

                    <button type="button" onClick={resetToDefault} className="login-back">
                      <ArrowLeft size={16} /> Back to {registerMode ? 'sign-up' : 'sign-in'} options
                    </button>
                  </>
                )}

                {mode === 'forgot' && (
                  <>
                    <h2>Reset your password</h2>
                    <p className="login-lead">
                      Enter your email and we&apos;ll send a link to choose a new password.
                    </p>

                    <form onSubmit={handleForgotPassword} className="login-email-form">
                      <label htmlFor="forgot-email-input">Email address</label>
                      <input
                        id="forgot-email-input"
                        type="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                        placeholder="you@example.com"
                        disabled={emailBusy}
                      />
                      {emailError && <p className="login-email-error">{emailError}</p>}
                      <button
                        type="submit"
                        disabled={emailBusy}
                        className="login-btn login-btn-google"
                      >
                        {emailBusy ? 'Sending…' : 'Send reset link'}
                      </button>
                    </form>

                    <button type="button" onClick={() => { setEmailError(''); setMode('email'); }} className="login-back">
                      <ArrowLeft size={16} /> Back to sign in
                    </button>
                  </>
                )}

                {mode === 'sent-reset' && (
                  <>
                    <div className="w-14 h-14 bg-[#043132]/10 text-[#043132] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail size={28} />
                    </div>
                    <h2>Check your email</h2>
                    <p className="login-lead" style={{ marginBottom: 12 }}>
                      If an account exists for <strong>{sentToEmail}</strong>, we&apos;ve sent a password reset link.
                    </p>
                    <p className="login-lead" style={{ fontSize: 12, marginTop: 0 }}>
                      If you do not see the email, check your spam folder. To help future messages reach your inbox, add{' '}
                      <strong>{AUTH_MAGIC_LINK_SENDER}</strong> to your contacts.
                    </p>
                    <button type="button" onClick={resetToDefault} className="login-back">
                      <ArrowLeft size={16} /> Back to sign in
                    </button>
                  </>
                )}

                {(mode === 'sent-link-google' || mode === 'sent-link-microsoft') && (
                  <>
                    <div className="w-14 h-14 bg-[#043132]/10 text-[#043132] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail size={28} />
                    </div>
                    <h2>Check your email</h2>
                    <p className="login-lead" style={{ marginBottom: 12 }}>
                      {mode === 'sent-link-google'
                        ? <>We&apos;ve sent a sign-in link to <strong>{sentToEmail}</strong> to confirm and link your Google account.</>
                        : <>We&apos;ve sent a sign-in link to <strong>{sentToEmail}</strong> to confirm and link your Microsoft account.</>}
                    </p>
                    <p className="login-lead" style={{ fontSize: 12, marginTop: 0 }}>
                      The link expires in about an hour. Open it in this same browser to skip the email-confirmation step.
                    </p>
                    <p className="login-lead" style={{ fontSize: 12, marginTop: 0 }}>
                      If you do not see the email, check your spam folder. To help future messages reach your inbox, add{' '}
                      <strong>{AUTH_MAGIC_LINK_SENDER}</strong> to your contacts.
                    </p>
                    <button type="button" onClick={resetToDefault} className="login-back">
                      <ArrowLeft size={16} /> Use a different email
                    </button>
                  </>
                )}
              </div>
            </EcoIntelLoadOverlay>

            {loginPageHtml.notice.trim() ? (
              <div
                className="login-notice"
                dangerouslySetInnerHTML={{ __html: loginPageHtml.notice }}
              />
            ) : null}
          </div>
        </section>
      </main>

      <footer
        className="login-app-footer"
        role="contentinfo"
        dangerouslySetInnerHTML={{ __html: loginPageHtml.footerInner }}
      />
    </div>
  );
}
